import { useSearchParams } from 'react-router-dom'
import { Menu, MenuButton } from '@headlessui/react'
import { Fragment, useState } from 'react'
import { useMediaQuery } from '@luuna/shared'
import { User } from '$api-client/types.gen'
import { PersonSelectFilterTriggerStyling } from './styling'
import { PersonSelectTrigger } from './trigger'
import { PersonSelectDesktopContent } from './desktop-content'
import { PersonSelectMobileContent } from './mobile-content'

export type ValueOf<T> = T[keyof T]

export const Strategy = {
  Query: 'query',
  State: 'state',
} as const

export type PersonSelectFilterProps = {
  options: User[]
  size?: NonNullable<PersonSelectFilterTriggerStyling>['size']
  title?: string
} & (
  | {
    strategy: typeof Strategy.Query
    queryParamKey: string
  }
  | {
    strategy: typeof Strategy.State
    value: number[]
    onChange: (selectedIds: number[]) => void
  }
)

export function PersonSelectFilter(props: PersonSelectFilterProps) {
  const { options, title } = props
  const strategy = props.strategy ?? Strategy.Query
  const size = props.size ?? 'md'
  const isMobile = useMediaQuery('(max-width: 639px)')
  const [isOpen, setOpen] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()

  const [internalSelectedPeopleIds, setInternalSelectedPeopleIds] = useState<number[]>(
    props.strategy === Strategy.State ? props.value : [],
  )

  const selectedPeopleIds = strategy === Strategy.Query && props.strategy === Strategy.Query
    ? searchParams.getAll(props.queryParamKey).map(Number)
    : internalSelectedPeopleIds

  const selectedPeople = options.filter(person => selectedPeopleIds.includes(person.id))

  const handleSelectPerson = (person: User) => {
    if (props.strategy === Strategy.Query) {
      return setSearchParams(prev => {
        const currentIds = prev.getAll(props.queryParamKey)

        if (currentIds.includes(person.id.toString())) {
          prev.delete(props.queryParamKey, person.id.toString())
        } else {
          prev.append(props.queryParamKey, String(person.id))
        }

        return prev
      })
    }

    if (props.strategy === Strategy.State) {
      const newSelectedIds = selectedPeopleIds.includes(person.id)
        ? selectedPeopleIds.filter(id => id !== person.id)
        : [...selectedPeopleIds, person.id]

      setInternalSelectedPeopleIds(newSelectedIds)
      props.onChange?.(newSelectedIds)
    }
  }

  const handleClearPerson = () => {
    if (props.strategy === Strategy.Query) {
      setSearchParams(prev => {
        prev.delete(props.queryParamKey)

        return prev
      })
    } else if (props.strategy === Strategy.State) {
      setInternalSelectedPeopleIds([])
      props.onChange([])
    }
  }

  if (isMobile) {
    return (
      <Fragment>
        <PersonSelectTrigger
          selectedPeople={selectedPeople}
          size={size}
          onClick={() => setOpen(true)}
        />

        <PersonSelectMobileContent
          title={title}
          isOpen={isOpen}
          onClose={() => setOpen(false)}
          options={options}
          selectedPeopleIds={selectedPeopleIds}
          onSelectPerson={handleSelectPerson}
          onClear={handleClearPerson}
        />
      </Fragment>
    )
  }

  return (
    <Menu>
      <MenuButton
        as={PersonSelectTrigger}
        selectedPeople={selectedPeople}
        size={size}
      />

      <PersonSelectDesktopContent
        options={options}
        selectedPeopleIds={selectedPeopleIds}
        onSelectPerson={handleSelectPerson}
        onClear={handleClearPerson}
      />
    </Menu>
  )
}
